import { useEffect, useRef, useState } from 'react';
import {
  asystent,
  type AsystentKorpus,
  type AsystentRozmowa,
  type AsystentWiadomosc,
} from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Asystent AI — rozmowa nad wybranym korpusem wiedzy.
 *
 * Układ trójdzielny: lista rozmów | konwersacja | panel „skąd to wiem". Ten trzeci nie jest
 * ozdobą — asystent odpowiada wyłącznie z materiałów i użytkownik musi móc sprawdzić, z których.
 * Odpowiedź bez możliwości weryfikacji źródła jest w tym zastosowaniu bezwartościowa.
 */
export function Asystent() {
  const [korpusy, setKorpusy] = useState<AsystentKorpus[]>([]);
  const [korpusId, setKorpusId] = useState<number | null>(null);
  const [rozmowy, setRozmowy] = useState<AsystentRozmowa[]>([]);
  const [aktywna, setAktywna] = useState<number | null>(null);
  const [wiadomosci, setWiadomosci] = useState<AsystentWiadomosc[]>([]);
  const [pytanie, setPytanie] = useState('');
  /** Rozmowa, w której właśnie generuje się odpowiedź (null = żadna). */
  const [czekaDla, setCzekaDla] = useState<number | null>(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [zrodlaDla, setZrodlaDla] = useState<number | null>(null);
  const czeka = czekaDla !== null;

  const dolStrony = useRef<HTMLDivElement>(null);
  /**
   * Licznik identyfikatorów tymczasowych dla wiadomości pokazanych optymistycznie.
   * Monotoniczny, a nie `-Date.now()`: dwa szybkie wysłania w tej samej milisekundzie dawały
   * ten sam klucz i React zgłaszał zduplikowane dzieci (a przy okazji mógł pogubić wiadomości).
   */
  const licznikTymczasowych = useRef(0);
  const nowyIdTymczasowy = () => --licznikTymczasowych.current;

  /**
   * Aktualnie OGLĄDANA rozmowa, czytana z wnętrza callbacków strumienia.
   *
   * Stan `aktywna` widziany w domknięciu pochodzi ze sprzed wysyłki, więc każdy asynchroniczny
   * `setWiadomosci` bez tego strażnika potrafił wpisać treść rozmowy A pod nagłówkiem rozmowy B.
   */
  const aktywnaRef = useRef<number | null>(null);
  const ustawAktywna = (id: number | null) => { aktywnaRef.current = id; setAktywna(id); };

  /** Przerwanie trwającego strumienia przy opuszczeniu widoku — bez tego czytnik żyje dalej. */
  const przerwanie = useRef<AbortController | null>(null);

  const wysylanie = useRef(false);   // strażnik natychmiastowy: `czekaDla` ustawia się dopiero po utworzeniu sesji

  useEffect(() => {
    asystent.korpusy().then(k => {
      setKorpusy(k);
      if (k.length > 0) setKorpusId(k[0].id);
    })
      .catch(e => setBlad(e?.message ?? 'Nie udało się wczytać korpusów'))
      .finally(() => setLadowanie(false));
    odswiezRozmowy();
    return () => przerwanie.current?.abort();
  }, []);

  useEffect(() => {
    dolStrony.current?.scrollIntoView({ behavior: 'smooth' });
  }, [wiadomosci, czekaDla]);

  const odswiezRozmowy = () =>
    asystent.rozmowy().then(setRozmowy).catch(() => {});

  const otworz = (id: number) => {
    ustawAktywna(id);
    setZrodlaDla(null);
    setBlad(null);
    asystent.wiadomosci(id)
      .then(w => { if (aktywnaRef.current === id) setWiadomosci(w); })
      .catch(e => setBlad(e?.message ?? 'Nie udało się wczytać rozmowy'));
  };

  /** Doładowanie historii z serwera, gdy widok rozjechał się ze strumieniem. */
  const przeladuj = (sesja: number) =>
    asystent.wiadomosci(sesja)
      .then(w => { if (aktywnaRef.current === sesja) setWiadomosci(w); })
      .catch(() => {});

  const wyslij = async (tekst: string) => {
    const trescPytania = tekst.trim();
    if (!trescPytania || wysylanie.current) return;
    wysylanie.current = true;
    setBlad(null);
    let sesja: number | null = aktywna;
    try {
      if (sesja === null) {
        if (korpusId === null) throw new Error('Wybierz asystenta');
        const nowa = await asystent.rozpocznij(korpusId);
        sesja = nowa.id;
        ustawAktywna(sesja);
      }
      setCzekaDla(sesja);
      // Pytanie pokazujemy od razu — czekanie na odpowiedź trwa i bez tego wygląda,
      // jakby kliknięcie nic nie zrobiło.
      setWiadomosci(w => [...w, {
        id: nowyIdTymczasowy(), role: 'USER', content: trescPytania, noAnswer: false,
        model: null, provider: null, error: null,
        createdAt: new Date().toISOString(), cytowania: [],
      }]);
      setPytanie('');

      // Odpowiedź budujemy przyrostowo w tymczasowej wiadomości o ujemnym id — po zakończeniu
      // strumienia podmieniamy ją na zapisaną wersję z serwera (z prawdziwym id i cytatami).
      const tymczasoweId = nowyIdTymczasowy();
      setWiadomosci(w => [...w, {
        id: tymczasoweId, role: 'ASSISTANT', content: '', noAnswer: false,
        model: null, provider: null, error: null,
        createdAt: new Date().toISOString(), cytowania: [],
      }]);

      const kontroler = new AbortController();
      przerwanie.current = kontroler;
      const wTejRozmowie = (fn: (w: AsystentWiadomosc[]) => AsystentWiadomosc[]) => {
        if (aktywnaRef.current === sesja) setWiadomosci(fn);
      };

      const odp = await asystent.zapytajStrumieniowo(
        sesja,
        trescPytania,
        fragment => wTejRozmowie(w => w.map(m =>
          m.id === tymczasoweId ? { ...m, content: m.content + fragment } : m)),
        // Materiały pokazujemy od razu — użytkownik widzi, na czym asystent pracuje,
        // zanim pojawi się pierwszy token.
        cytaty => {
          wTejRozmowie(w => w.map(m => m.id === tymczasoweId ? { ...m, cytowania: cytaty } : m));
          if (aktywnaRef.current === sesja) setZrodlaDla(tymczasoweId);
        },
        kontroler.signal,
      );

      // Wiadomość tymczasowa mogła zniknąć — użytkownik przełączył się na inną rozmowę i wrócił,
      // a wtedy widok pochodzi już z serwera. Bez tej gałęzi gotowa odpowiedź przepadała po cichu.
      if (aktywnaRef.current === sesja) {
        setWiadomosci(w => w.some(m => m.id === tymczasoweId)
          ? w.map(m => (m.id === tymczasoweId ? odp : m))
          : [...w.filter(m => m.id !== odp.id), odp]);
        setZrodlaDla(z => (z === tymczasoweId ? odp.id : z));
      }
      odswiezRozmowy();
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        setBlad(e?.message ?? 'Nie udało się uzyskać odpowiedzi');
      }
      // Cofnij wszystko, co dodaliśmy optymistycznie (ujemne id) — serwer zapisał własną wersję
      // (także urwaną odpowiedź z adnotacją o błędzie), więc trzymanie tu połówki tylko myli.
      setWiadomosci(w => w.filter(m => m.id > 0));
      // `sesja` z lokalnej zmiennej, NIE stan `aktywna` z domknięcia: przy pierwszym pytaniu
      // w świeżej rozmowie ten stan jest jeszcze nullem i historia nigdy się nie doładowywała —
      // rozmowa wyglądała na pustą i znikała z listy do czasu przeładowania strony.
      if (sesja !== null) void przeladuj(sesja);
      void odswiezRozmowy();
    } finally {
      wysylanie.current = false;
      przerwanie.current = null;
      setCzekaDla(null);
    }
  };

  const usunRozmowe = async (id: number) => {
    if (!window.confirm('Usunąć tę rozmowę? Historii nie da się odzyskać.')) return;
    try {
      await asystent.usun(id);
    } catch (e: any) {
      setBlad(e?.message ?? 'Nie udało się usunąć rozmowy');
      return;   // bez tego wyjątek przerywał funkcję i lista nigdy się nie odświeżała
    }
    if (aktywna === id) { ustawAktywna(null); setWiadomosci([]); setZrodlaDla(null); }
    void odswiezRozmowy();
  };

  const wybrany = korpusy.find(k => k.id === korpusId) ?? null;
  const przyklady: string[] = (() => {
    try { return wybrany?.sampleQuestions ? JSON.parse(wybrany.sampleQuestions) : []; }
    catch { return []; }
  })();
  const cytatyPanelu = wiadomosci.find(m => m.id === zrodlaDla)?.cytowania ?? [];

  // Trzy stany, nie jeden. Wcześniej pusta lista znaczyła jednocześnie „ładuje się", „padło
  // zapytanie" i „brak dostępu" — wygasła sesja albo 500 wyglądały jak brak uprawnień i wysyłały
  // użytkownika do administratora zamiast kazać mu ponowić.
  if (ladowanie) {
    return <div className="card p-8 text-center text-ink-400 text-sm">Wczytywanie asystentów…</div>;
  }
  if (korpusy.length === 0) {
    return (
      <div className="card p-8 text-center">
        {blad ? (
          <div className="text-red-500 text-sm">
            Nie udało się wczytać listy asystentów: {blad}
          </div>
        ) : (
          <div className="text-ink-500 text-sm">
            Nie masz dostępu do żadnego korpusu wiedzy. Poproś administratora o nadanie dostępu
            w <span className="font-semibold">Korpusy wiedzy → Dostęp</span>.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-4 h-[calc(100vh-140px)]">
      {/* ── Rozmowy ─────────────────────────────────────────────── */}
      <aside className="card w-64 flex-none flex flex-col overflow-hidden">
        <div className="card-head">
          <div className="card-title">Rozmowy</div>
        </div>
        <div className="p-3 border-b border-ink-100">
          <SearchableSelect
            value={korpusId === null ? '' : String(korpusId)}
            onChange={v => { setKorpusId(v ? Number(v) : null); ustawAktywna(null); setWiadomosci([]); }}
            options={korpusy.map(k => ({ value: String(k.id), label: k.name }))}
            placeholder="Wybierz asystenta"
          />
          <button
            className="btn btn-water w-full mt-2 text-[13px]"
            onClick={() => { ustawAktywna(null); setWiadomosci([]); setZrodlaDla(null); }}>
            <Icon name="plus" /> Nowa rozmowa
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {rozmowy.length === 0 && (
            <div className="p-4 text-[12px] text-ink-400">Brak zapisanych rozmów.</div>
          )}
          {rozmowy.map(r => (
            <div
              key={r.id}
              onClick={() => otworz(r.id)}
              className={'px-3 py-2 border-b border-ink-100 cursor-pointer hover:bg-ink-50 '
                + (aktywna === r.id ? 'bg-water-500/5 border-l-2 border-l-water-500' : '')}>
              <div className="text-[13px] text-ink-800 line-clamp-2">
                {r.title ?? 'Nowa rozmowa'}
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-ink-400">{r.corpusName}</span>
                <button
                  className="btn-ghost text-[10px] px-1"
                  title="Usuń rozmowę"
                  onClick={e => { e.stopPropagation(); usunRozmowe(r.id); }}>
                  <Icon name="x" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ── Konwersacja ─────────────────────────────────────────── */}
      <section className="card flex-1 flex flex-col overflow-hidden">
        <div className="card-head">
          <div className="card-title">{wybrany?.name ?? 'Asystent AI'}</div>
          {wybrany && (
            <span className="pill pill-gray ml-auto">
              {wybrany.sourceCount} źródeł · {wybrany.fragmentCount} fragmentów
            </span>
          )}
        </div>

        {blad && (
          <div className="mx-4 mt-3 px-3 py-2 rounded bg-red-100 text-red-500 text-[12px]">{blad}</div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {wiadomosci.length === 0 && !czeka && (
            <div className="text-center text-ink-400 text-[13px] pt-8">
              <div className="mb-4">{wybrany?.description}</div>
              {przyklady.length > 0 && (
                <div className="flex flex-col items-center gap-2">
                  <div className="text-[11px] uppercase tracking-wider text-ink-300">
                    Przykładowe pytania
                  </div>
                  {przyklady.map((p, i) => (
                    <button key={i} className="btn btn-secondary text-[12px]"
                            onClick={() => wyslij(p)}>{p}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {wiadomosci.map(m => (
            <div key={m.id} className={m.role === 'USER' ? 'flex justify-end' : ''}>
              <div className={m.role === 'USER'
                ? 'max-w-[75%] bg-ink-900 text-white rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap'
                : 'max-w-[85%]'}>
                {m.role === 'ASSISTANT' ? (
                  <div className={'rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap border '
                    + (m.noAnswer ? 'bg-amber-100/40 border-amber-500/30'
                       : m.error ? 'bg-red-100/40 border-red-500/30'
                       : 'bg-white border-ink-100')}>
                    {m.content}
                    {m.error && (
                      <div className="mt-2 text-[11px] text-red-500">Szczegóły: {m.error}</div>
                    )}
                    <div className="mt-2 flex items-center gap-3">
                      {m.cytowania.length > 0 && (
                        <button className="ai-cite" onClick={() => setZrodlaDla(m.id)}>
                          Skąd to wiem ({m.cytowania.length})
                        </button>
                      )}
                      {m.model && <span className="text-[10px] text-ink-300">{m.model}</span>}
                    </div>
                  </div>
                ) : m.content}
              </div>
            </div>
          ))}

          {czeka && czekaDla === aktywna && wiadomosci.every(m => m.id > 0 || m.role === 'USER') && (
            <div className="text-[12px] text-ink-400">Szukam w materiałach…</div>
          )}
          <div ref={dolStrony} />
        </div>

        <div className="border-t border-ink-100 p-3 flex gap-2">
          <textarea
            className="input flex-1 resize-none"
            rows={2}
            placeholder={czeka && czekaDla !== aktywna
              ? 'Asystent odpowiada w innej rozmowie…' : 'Zadaj pytanie…'}
            value={pytanie}
            disabled={czeka}
            onChange={e => setPytanie(e.target.value)}
            onKeyDown={e => {
              // Enter wysyła, Shift+Enter łamie linię — tak działa każdy czat i inaczej
              // użytkownik traci pytanie, próbując zrobić akapit.
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); wyslij(pytanie); }
            }}
          />
          <button className="btn btn-water self-end" disabled={czeka || !pytanie.trim()}
                  onClick={() => wyslij(pytanie)}>
            Wyślij
          </button>
        </div>
      </section>

      {/* ── Skąd to wiem ────────────────────────────────────────── */}
      {cytatyPanelu.length > 0 && (
        <aside className="card w-80 flex-none flex flex-col overflow-hidden">
          <div className="card-head">
            <div className="card-title">Skąd to wiem</div>
            <button className="btn-ghost ml-auto text-[11px]" onClick={() => setZrodlaDla(null)}>
              <Icon name="x" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {cytatyPanelu.map(c => (
              // Klucz po markerze, nie po id: cytaty ze zdarzenia `sources` są zapowiedziami
              // sprzed zapisu i mają id = null, więc kluczowanie po nim dawało komplet
              // identycznych kluczy. Marker ([Z-1], [Z-2]…) jest unikalny w obrębie odpowiedzi.
              <div key={c.marker} className="border border-ink-100 rounded p-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="ai-cite">{c.marker}</span>
                  {c.vecScore !== null && (
                    <span className="text-[10px] text-ink-400 font-mono tabular-nums">
                      {c.vecScore.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="text-[12px] font-semibold text-ink-800">{c.label}</div>
                {c.snippet && (
                  <div className="text-[11px] text-ink-500 mt-1 line-clamp-6">{c.snippet}</div>
                )}
                {c.targetRoute && (
                  <a href={c.targetRoute} className="text-[11px] text-water-600 mt-1 inline-block">
                    Pokaż źródło →
                  </a>
                )}
              </div>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}
